import React, { useEffect, useMemo, useState } from 'react';
import './Reservations.css';
import {
  createExpense,
  deleteExpense,
  getExpenses,
  updateExpense
} from '../../services/expenseService';
import ExpenseModal from '../../components/modals/ExpenseModal';
import ExpensesFiltersModal from '../../components/modals/ExpensesFiltersModal';
import Table from '../../components/common/Table';
import { formatDate } from '../../utils/date';
import '../../components/common/IconButton.css';
import { IconFilter, IconPencil } from '../../components/common/Icons';

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [queryFilter, setQueryFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const formatAmount = (value, currency) => {
    if (value === null || value === undefined || value === '') return '-';
    const n = Number(value);
    if (Number.isNaN(n)) return '-';
    return currency === '$' ? n.toFixed(0) : n.toFixed(2);
  };

  const formatMoney = (value, currency) => {
    const amount = formatAmount(value, currency);
    if (amount === '-') return '-';
    return currency ? `${currency} ${amount}` : amount;
  };

  const loadExpenses = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getExpenses();
      setExpenses(data);
    } catch (err) {
      console.error(err);
      setError('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  const filtered = useMemo(() => {
    const q = queryFilter.toLowerCase();
    return (expenses || []).filter((e) => {
      const date = e.date?.slice(0, 10) || '';

      if (dateFrom && (!date || date < dateFrom)) return false;
      if (dateTo && (!date || date > dateTo)) return false;
      if (currencyFilter && e.currency !== currencyFilter) return false;

      return `${e.date || ''} ${e.description || ''} ${e.value || ''} ${e.currency || ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [expenses, queryFilter, dateFrom, dateTo, currencyFilter]);

  const handleCreate = () => {
    setSelected(null);
    setModalOpen(true);
  };

  const handleEdit = (expense) => {
    setSelected(expense);
    setModalOpen(true);
  };

  const handleSave = async (data) => {
    setError('');
    try {
      if (selected) {
        await updateExpense(selected.id, data);
      } else {
        await createExpense(data);
      }
      setModalOpen(false);
      setSelected(null);
      await loadExpenses();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudo guardar el gasto');
    }
  };

  const handleDelete = async (id) => {
    setError('');
    try {
      await deleteExpense(id);
      setModalOpen(false);
      setSelected(null);
      await loadExpenses();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudo eliminar el gasto');
    }
  };

  const columns = [
    {
      id: 'date',
      header: 'Fecha',
      accessor: (e) => formatDate(e.date, ''),
      sortAccessor: (e) => e.date || '',
      filterPlaceholder: 'YYYY-MM-DD'
    },
    {
      id: 'description',
      header: 'Descripción',
      accessor: (e) => e.description || '',
      filterPlaceholder: 'Filtrar descripción'
    },
    {
      id: 'isFromFede',
      header: '',
      sortable: false,
      filterable: false,
      headerClassName: 'expense-from-fede-column',
      cell: (e) => Number(e.isFromFede) === 1
        ? <span className="expense-from-fede-marker" aria-label="Gasto de Fede">F</span>
        : null
    },
    {
      id: 'money',
      header: 'Importe',
      accessor: (e) => formatMoney(e.value, e.currency),
      sortComparator: (a, b) => {
        const ca = a?.currency ?? '';
        const cb = b?.currency ?? '';
        const cRes = String(ca).localeCompare(String(cb), undefined, { sensitivity: 'base' });
        if (cRes !== 0) return cRes;
        const va = Number(a?.value);
        const vb = Number(b?.value);
        const aNaN = Number.isNaN(va);
        const bNaN = Number.isNaN(vb);
        if (aNaN && bNaN) return 0;
        if (aNaN) return 1;
        if (bNaN) return -1;
        return va - vb;
      },
      filterPlaceholder: 'Filtrar importe'
    },
    {
      id: 'actions',
      header: 'Acciones',
      headerClassName: 'th-center',
      sortable: false,
      filterable: false,
      cell: (e) => (
        <div className="inline-actions">
          <button
            type="button"
            className="icon-btn icon-btn--edit"
            onClick={() => handleEdit(e)}
            aria-label="Editar gasto"
            title="Editar"
          >
            <IconPencil />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <div className="actions-row">
          <h1>Gastos</h1>
          <button
            type="button"
            className="icon-btn icon-btn--edit"
            onClick={() => setFiltersOpen(true)}
            aria-label="Filtrar gastos"
            title="Filtrar"
          >
            <IconFilter />
          </button>
        </div>
        <div className="actions-row">
          <input
            className="search-input"
            placeholder="Buscar gasto"
            value={queryFilter}
            onChange={(e) => setQueryFilter(e.target.value)}
          />
          <button className="btn" onClick={handleCreate}>+ Nuevo Gasto</button>
        </div>
      </div>

      {error && <div className="form-error-message" role="alert">{error}</div>}

      {loading ? (
        <p>Cargando...</p>
      ) : (
        <Table
          data={filtered}
          columns={columns}
          rowKey="id"
          className="table expenses-table"
          emptyMessage="No hay gastos para mostrar."
        />
      )}

      <ExpenseModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelected(null); }}
        onSave={handleSave}
        onDelete={handleDelete}
        initialData={selected}
      />

      <ExpensesFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        initialDateFrom={dateFrom}
        initialDateTo={dateTo}
        initialCurrency={currencyFilter}
        onApply={({ dateFrom: df, dateTo: dt, currency }) => {
          setDateFrom(df || '');
          setDateTo(dt || '');
          setCurrencyFilter(currency || '');
          setFiltersOpen(false);
        }}
      />
    </div>
  );
};

export default ExpensesPage;

